<!--
  @component EphemeralIO — P4 effecting lens (Decision B preview envs).
  chain:  ctx.deployment.dispatch("ephemeral.*", …) → this → ctx.ephemeral
  effects: spin (webhook/manual) + teardown (TTL/manual dual mechanism).
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { EPHEMERAL_IO_KEY } from "./deployment-io.js";
  import type { EphemeralIOContext } from "./deployment-io.js";
  export { EPHEMERAL_IO_KEY };
  export type { EphemeralIOContext };
  export function useEphemeralIO(): EphemeralIOContext {
    const ctx = getContext<EphemeralIOContext | undefined>(EPHEMERAL_IO_KEY);
    if (!ctx) throw new Error("useEphemeralIO() called outside of an EphemeralIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: EphemeralIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("ephemeral.")); },
    get live() { return parent.stream.filter((e) => e.op.startsWith("ephemeral.")); },
    spin: (branchRef, options?) => dispatchOp("ephemeral.spin", { branchRef, options }),
    teardown: (envId) => dispatchOp("ephemeral.teardown", { envId }),
  } satisfies EphemeralIOContext;

  setContext(EPHEMERAL_IO_KEY, ctx);
</script>
{@render children?.()}
